/**
 * Print utility for job card details
 * Opens a new window with a print-friendly view of the job card
 */
export const printJobCard = (jobCard) => {
  if (!jobCard) return;

  const normalLines = jobCard.normal_pressing_lines || [];
  const hotLines = jobCard.hot_pressing_lines || [];
  const veneerLines = jobCard.veneer_pressing_lines || [];
  const finishingLines = (jobCard.finishing_lines || []).filter(l => l.qty > 0);

  const renderPressingTable = (lines, title, color) => {
    if (!lines.length) return '';
    return `
      <div class="section">
        <h3 style="color: ${color}; margin-bottom: 6px;">${title}</h3>
        <table>
          <thead><tr style="background: ${color}; color: white;">
            <th>S.No</th><th>Description</th><th>Ply Qty</th><th>Side</th><th>Total</th><th>Details</th><th>Status</th><th>Machine</th>
          </tr></thead>
          <tbody>
            ${lines.map((l, i) => `
              <tr${l.highlighted ? ' style="background:#86EFAC"' : ''}>
                <td style="text-align:center">${i + 1}</td>
                <td>${l.description || '-'}</td>
                <td style="text-align:center">${l.ply_qty || 0}</td>
                <td style="text-align:center">${l.side || 1}</td>
                <td style="text-align:center;font-weight:bold">${l.total || 0}</td>
                <td>${l.details || '-'}</td>
                <td style="text-align:center">${(l.status || 'not_started').replace(/_/g, ' ')}</td>
                <td style="text-align:center">${l.machine_type ? l.machine_type.toUpperCase() : '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  };

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Job Card ${jobCard.job_card_number}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; color: #1a1a1a; font-size: 12px; }
        .header { text-align: center; border-bottom: 3px solid #1E40AF; padding-bottom: 10px; margin-bottom: 15px; }
        .header h1 { color: #1E40AF; font-size: 20px; margin-bottom: 2px; }
        .header p { color: #666; font-size: 11px; }
        .banner { background: #1E40AF; color: white; text-align: center; padding: 8px; font-size: 14px; font-weight: bold; margin-bottom: 15px; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 15px; background: #f9fafb; padding: 10px; border: 1px solid #e5e7eb; }
        .info-item { display: flex; gap: 6px; }
        .info-label { font-weight: 600; color: #374151; min-width: 100px; }
        .info-value { color: #111; }
        .section { margin-bottom: 12px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 11px; }
        th, td { border: 1px solid #ccc; padding: 5px 8px; }
        th { font-weight: 600; }
        .summary-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0; text-align: center; margin-bottom: 15px; }
        .summary-box { border: 1px solid #ccc; padding: 10px; }
        .summary-box .label { font-size: 11px; color: #666; }
        .summary-box .value { font-size: 18px; font-weight: bold; color: #111; }
        .footer { text-align: center; color: #999; font-size: 9px; margin-top: 20px; border-top: 1px solid #ddd; padding-top: 8px; }
        @media print { body { padding: 10px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Interior Job Work</h1>
        <p>Furniture Production Management</p>
      </div>
      <div class="banner">${jobCard.card_type === 'pressing' ? 'JOB CARD - PRESSING' : jobCard.card_type === 'work_details' ? 'WORK DETAILS' : 'JOB CARD'}</div>
      
      <div class="info-grid">
        <div class="info-item"><span class="info-label">Job Card No:</span><span class="info-value">${jobCard.job_card_number || ''}</span></div>
        <div class="info-item"><span class="info-label">Date:</span><span class="info-value">${jobCard.date || ''}</span></div>
        <div class="info-item"><span class="info-label">Customer:</span><span class="info-value">${jobCard.customer_name || ''}</span></div>
        <div class="info-item"><span class="info-label">Delivery Date:</span><span class="info-value">${jobCard.delivery_date || '-'}</span></div>
        <div class="info-item"><span class="info-label">Reference:</span><span class="info-value">${jobCard.reference || '-'}</span></div>
        <div class="info-item"><span class="info-label">Status:</span><span class="info-value">${(jobCard.status || '').replace(/_/g, ' ')}</span></div>
        <div class="info-item"><span class="info-label">Mobile:</span><span class="info-value">${jobCard.mobile_no || '-'}</span></div>
        <div class="info-item"><span class="info-label">Coordinator:</span><span class="info-value">${jobCard.created_by_name || '-'}</span></div>
        <div class="info-item"><span class="info-label">Work In-Charge:</span><span class="info-value">${jobCard.work_in_charge || '-'}</span></div>
      </div>

      ${renderPressingTable(normalLines, 'Normal Pressing', '#1E40AF')}
      ${renderPressingTable(hotLines, 'Hot Pressing', '#EA580C')}
      ${renderPressingTable(veneerLines, 'PVC & Acrylic Pressing', '#7C3AED')}

      ${(jobCard.pressing_images && jobCard.pressing_images.length > 0) ? `
        <div class="section">
          <h3 style="color: #EA580C; margin-bottom: 6px;">Pressing Diagrams</h3>
          <div style="display: flex; flex-wrap: wrap; gap: 10px;">
            ${jobCard.pressing_images.map((url, idx) => `
              <img src="${window.location.origin}${url}" alt="Pressing diagram ${idx + 1}" style="max-width: 200px; max-height: 150px; border: 1px solid #ccc; border-radius: 4px; object-fit: contain;" />
            `).join('')}
          </div>
        </div>
      ` : ''}

      <div class="section">
        <h3 style="color: #0891B2; margin-bottom: 6px;">Work Quantity Summary</h3>
        <div class="summary-grid">
          <div class="summary-box"><div class="label">Cutting Qty</div><div class="value">${jobCard.cutting_qty_total || 0}</div></div>
          <div class="summary-box"><div class="label">Pressing Qty</div><div class="value">${jobCard.pressing_qty_total || 0}</div></div>
          <div class="summary-box"><div class="label">Boring Qty</div><div class="value">${jobCard.boring_qty_total || 0}</div></div>
        </div>
      </div>

      ${(jobCard.cutting_status || jobCard.boring_status) ? `
        <div class="section">
          <h3 style="color: #0D9488; margin-bottom: 6px;">Operator Status</h3>
          <table>
            <thead><tr style="background: #0D9488; color: white;">
              <th>Operation</th><th>Status</th><th>Quantity</th><th>Operator Notes</th>
            </tr></thead>
            <tbody>
              <tr>
                <td style="text-align:center">Cutting</td>
                <td style="text-align:center">${(jobCard.cutting_status || 'N/A').replace(/_/g, ' ')}</td>
                <td style="text-align:center">${jobCard.cutting_qty_total || 0}</td>
                <td>${jobCard.cutting_operator_note || '-'}</td>
              </tr>
              <tr>
                <td style="text-align:center">Boring</td>
                <td style="text-align:center">${(jobCard.boring_status || 'N/A').replace(/_/g, ' ')}</td>
                <td style="text-align:center">${jobCard.boring_qty_total || 0}</td>
                <td>${jobCard.boring_operator_note || '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ` : ''}

      ${finishingLines.length ? `
        <div class="section">
          <h3 style="color: #DB2777; margin-bottom: 6px;">Finishing Works</h3>
          <table>
            <thead><tr style="background: #DB2777; color: white;">
              <th>S.No</th><th>Description</th><th>Unit</th><th>Qty</th><th>Rate</th><th>Total</th>
            </tr></thead>
            <tbody>
              ${finishingLines.map((l, i) => `
                <tr>
                  <td style="text-align:center">${i + 1}</td>
                  <td>${l.description || ''}</td>
                  <td style="text-align:center">${l.unit || 'Nos'}</td>
                  <td style="text-align:center">${l.qty || 0}</td>
                  <td style="text-align:right">${(l.rate || 0).toFixed(2)}</td>
                  <td style="text-align:right;font-weight:bold">${(l.total || 0).toFixed(2)}</td>
                </tr>
              `).join('')}
              <tr>
                <td colspan="4"></td>
                <td style="text-align:right;font-weight:bold">Grand Total:</td>
                <td style="text-align:right;font-weight:bold">${finishingLines.reduce((s, l) => s + (l.total || 0), 0).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ` : ''}

      <div class="footer">Generated on ${new Date().toLocaleString()} | Interior Job Work</div>
      <script>window.onload = function() { window.print(); }</script>
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  }
};
